import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircleOutline as CheckIcon, ErrorOutline as ErrIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import { BRAND, ROOT_DOMAIN, Lnk, usePageMeta } from './shared';

const ORG_TYPES = [
  'Fraternity', 'Sorority', 'Civic Organization', 'Alumni Association',
  'Professional Association', 'Nonprofit', 'Other',
];

type SubState = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error';

const slugify = (s: string) =>
  s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-').slice(0, 30).replace(/-+$/g, '');

const initial = {
  orgName: '', orgType: '', subdomain: '',
  primaryColor: '#A6192E', secondaryColor: '#D4A11E',
  ownerFirstName: '', ownerLastName: '', ownerEmail: '', estimatedMembers: '', message: '',
};

const GetStarted: React.FC = () => {
  usePageMeta('Start your free trial');
  const query = new URLSearchParams(useLocation().search);
  const plan = query.get('plan') || '';

  const previousStep = useRef(1);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [subState, setSubState] = useState<SubState>('idle');
  const [subMsg, setSubMsg] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'err'>('idle');
  const [error, setError] = useState('');
  const [host, setHost] = useState('');

  useEffect(() => {
    if (previousStep.current !== step) {
      document.querySelector<HTMLInputElement>('.form-card input')?.focus();
      previousStep.current = step;
    }
  }, [step]);

  const set = (k: keyof typeof initial) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Live subdomain availability (debounced) while on step 2.
  useEffect(() => {
    if (step !== 2 || !form.subdomain) { setSubState('idle'); setSubMsg(''); return; }
    setSubState('checking'); setSubMsg('');
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/provisioning/check-subdomain?subdomain=${encodeURIComponent(form.subdomain)}`);
        if (!res.ok) {
          setSubState('error');
          setSubMsg("Couldn't check availability right now. Please try again.");
          return;
        }
        const data = await res.json();
        if (!data.valid) { setSubState('invalid'); setSubMsg(data.reason || 'Invalid subdomain.'); }
        else if (data.available) { setSubState('available'); setSubMsg(`${data.host} is available`); }
        else { setSubState('taken'); setSubMsg(data.reason || 'That subdomain is taken.'); }
      } catch {
        setSubState('error');
        setSubMsg("Couldn't check availability. Check your connection and try again.");
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.subdomain, step]);

  const goStep2 = () => {
    if (!form.orgName.trim() || !form.orgType) { setError('Please enter your organization name and type.'); return; }
    setError('');
    setForm((f) => ({ ...f, subdomain: f.subdomain || slugify(f.orgName) }));
    setStep(2);
  };
  const goStep3 = () => {
    if (subState !== 'available') { setError('Please choose an available subdomain.'); return; }
    setError(''); setStep(3);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.ownerFirstName.trim() || !form.ownerEmail.trim()) {
      setStatus('err'); setError('Please enter your name and work email.'); return;
    }
    setStatus('sending'); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/provisioning/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgName: form.orgName, orgType: form.orgType, subdomain: form.subdomain,
          branding: { primaryColor: form.primaryColor, secondaryColor: form.secondaryColor },
          ownerFirstName: form.ownerFirstName, ownerLastName: form.ownerLastName, ownerEmail: form.ownerEmail,
          estimatedMembers: form.estimatedMembers, plan, message: form.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setHost(data.host || `${form.subdomain}.${ROOT_DOMAIN}`);
      setStatus('ok');
    } catch (err: any) {
      setStatus('err'); setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  if (status === 'ok') {
    return (
      <section className="page-body">
        <div className="container narrow">
          <div className="form-card center" role="status">
            <CheckIcon style={{ color: '#1E7A45', fontSize: 52 }} aria-hidden="true" />
            <h1 style={{ marginTop: 12 }}>Request received</h1>
            <p className="muted">
              Thanks, {form.ownerFirstName}. We’re reviewing your request for <strong>{form.orgName}</strong> at{' '}
              <strong>{host}</strong>. You’ll get an email shortly with the next steps to activate your free trial.
            </p>
            <Lnk to="/" className="btn btn-primary" style={{ marginTop: 18, justifyContent: 'center' }}>Back to home</Lnk>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="page-head">
        <div className="container">
          <p className="kicker">
            Start your free trial{plan ? ` · ${plan}` : ''}
          </p>
          <h1>Create your community on {BRAND}.</h1>
          <p>Three quick steps. No card required. Your 14-day trial starts once we activate your community.</p>
        </div>
      </section>

      <section className="page-body">
        <div className="container narrow">
          <ol className="wizard-steps" aria-label="Setup progress">
            {['Organization', 'Address & brand', 'Your details'].map((label, i) => (
              <li key={label} aria-current={step === i + 1 ? 'step' : undefined} className={`wizard-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>
                <span className="wizard-dot">{step > i + 1 ? '✓' : i + 1}</span>{label}
              </li>
            ))}
          </ol>

          <form className="form-card" onSubmit={submit}>
            {error && <div className="form-msg err" role="alert">{error}</div>}

            {step === 1 && (
              <>
                <div className="field">
                  <label htmlFor="g-name">Organization name *</label>
                  <input id="g-name" required value={form.orgName} onChange={set('orgName')} placeholder="e.g. Acme Alumni Association" />
                </div>
                <div className="field">
                  <label htmlFor="g-type">Organization type *</label>
                  <select id="g-type" required value={form.orgType} onChange={set('orgType')}>
                    <option value="">Select one…</option>
                    {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-primary btn-block" onClick={goStep2}>Continue</button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="field">
                  <label htmlFor="g-sub">Choose your address *</label>
                  <div className="subdomain-input">
                    <input id="g-sub" required aria-describedby="g-sub-status" aria-invalid={subState === 'taken' || subState === 'invalid'} value={form.subdomain}
                      onChange={(e) => setForm((f) => ({ ...f, subdomain: slugify(e.target.value) }))}
                      placeholder="your-org" autoCapitalize="none" spellCheck={false} />
                    <span className="subdomain-suffix">.{ROOT_DOMAIN}</span>
                  </div>
                  <p id="g-sub-status" role="status" className={`subdomain-hint ${subState === 'error' ? 'invalid' : subState}`}>
                      {subState === 'checking' && 'Checking availability…'}
                      {subState === 'available' && <><CheckIcon style={{ fontSize: 16 }} /> {subMsg}</>}
                      {(subState === 'taken' || subState === 'invalid' || subState === 'error') && <><ErrIcon style={{ fontSize: 16 }} /> {subMsg}</>}
                  </p>
                </div>

                <div className="field">
                  <span className="label">Brand colors</span>
                  <div className="color-row">
                    <label className="color-pick">
                      <input type="color" value={form.primaryColor} onChange={set('primaryColor')} />
                      <span>Primary<br /><code>{form.primaryColor}</code></span>
                    </label>
                    <label className="color-pick">
                      <input type="color" value={form.secondaryColor} onChange={set('secondaryColor')} />
                      <span>Accent<br /><code>{form.secondaryColor}</code></span>
                    </label>
                    <div className="color-preview" style={{ background: form.primaryColor }}>
                      <span style={{ color: form.secondaryColor }}>Aa</span>
                    </div>
                  </div>
                  <p className="muted xs" style={{ marginTop: 6 }}>You can upload a logo and fine-tune branding after activation.</p>
                </div>

                <div className="wizard-nav">
                  <button type="button" className="btn btn-outline" onClick={() => { setError(''); setStep(1); }}>Back</button>
                  <button type="button" className="btn btn-primary" onClick={goStep3} disabled={subState !== 'available'}>Continue</button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="g-fn">First name *</label>
                    <input id="g-fn" required value={form.ownerFirstName} onChange={set('ownerFirstName')} autoComplete="given-name" />
                  </div>
                  <div className="field">
                    <label htmlFor="g-ln">Last name</label>
                    <input id="g-ln" value={form.ownerLastName} onChange={set('ownerLastName')} autoComplete="family-name" />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="g-email">Work email *</label>
                  <input id="g-email" required type="email" value={form.ownerEmail} onChange={set('ownerEmail')} autoComplete="email" />
                </div>
                <div className="field">
                  <label htmlFor="g-est">Estimated active members</label>
                  <input id="g-est" type="number" min={0} value={form.estimatedMembers} onChange={set('estimatedMembers')} placeholder="e.g. 150" />
                </div>
                <div className="field">
                  <label htmlFor="g-msg">Anything we should know?</label>
                  <textarea id="g-msg" value={form.message} onChange={set('message')} placeholder="Tell us about your organization." />
                </div>
                <div className="wizard-nav">
                  <button type="button" className="btn btn-outline" onClick={() => { setError(''); setStep(2); }}>Back</button>
                  <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Submitting…' : 'Request my trial'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </section>
    </>
  );
};

export default GetStarted;
